import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Card, CardContent, Grid, Button, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Alert, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, FormControlLabel, Switch, Select, MenuItem,
  FormControl, InputLabel, Tabs, Tab, Checkbox, ListItemText, OutlinedInput,
  Tooltip, Divider
} from '@mui/material';
import {
  Settings as SettingsIcon, Refresh as RefreshIcon, Edit as EditIcon,
  Search as SearchIcon, Add as AddIcon, Delete as DeleteIcon,
  Warning as WarningIcon, Computer as ComputerIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import Funcionarios from './Funcionarios';
import Setores from './Setores';
import CadastroUsuario from './CadastroUsuario';

const TIPOS_ETAPA = ['PRODUCAO', 'CONFERENCIA', 'VISTO', 'LOGISTICA', 'ENTREGA_FINAL', 'OUTRO'];
const UNIDADES = ['MATRIZ', 'CABO FRIO', 'RIO DAS OSTRAS', 'MARILEIA', 'CASIMIRO', 'BARRA'];

const CORES_TIPO = {
  PRODUCAO: '#22c55e',
  CONFERENCIA: '#3b82f6',
  VISTO: '#8b5cf6',
  LOGISTICA: '#f97316',
  ENTREGA_FINAL: '#ef4444',
  OUTRO: '#94a3b8'
};

const TIPOS_LOG = [
  'BIPE_OK', 'RECUSA_ETAPA_DUPLICADA', 'RECUSA_ETAPA_INCOMPATIVEL',
  'RECUSA_FORMA_INCOMPATIVEL', 'RECUSA_POS_FINAL',
  'RECUSA_SEM_VISTO', 'RECUSA_SETOR_NAO_AUTORIZADO', 'RECUSA_IP',
  'TROCA_USUARIO', 'TROCA_SETOR',
  'RECUSA_LOTE_DUPLICADO', 'RECUSA_POS_CONFERENCIA',
  'EDICAO_REQUISICAO', 'EXCLUSAO_REQUISICAO', 'EXCLUSAO_ORDEM_PRODUCAO'
];

// Indice de cada aba. Existe porque o indice era comparado direto (aba === 3)
// em pontos distantes do arquivo: inserir uma aba no meio quebrava a tela de
// Logs em silencio. Manter em sincronia com a ordem dos <Tab> abaixo.
const ABA = {
  ETAPA: 0, PARAMETROS: 1, ATRELAR_SETOR: 2, TRAVA_IP: 3,
  LOGS: 4, FUNCIONARIOS: 5, SETORES: 6, USUARIOS: 7
};

// As travas de forma farmaceutica tem estado proprio, e nao um liga/desliga:
// o sistema ja esta em modo bloqueio, entao uma regra nova precisa poder ser
// medida antes de comecar a recusar.
// As duas metades da aba Parametros. A divisao e de leitura: as chaves sao as
// mesmas nos dois mundos, com excecao das que so existem na ordem de producao.
const SUBABA = { REQUISICAO: 0, ORDEM_PRODUCAO: 1 };

// Ordem dos estados, para o "menor entre o mestre e a regra". Fica aqui, no
// escopo de modulo, junto das demais constantes — foi por estar solto no corpo
// do componente que os valores legados '1'/'0' ficaram de fora.
const ORDEM_ESTADO = { off: 0, log: 1, bloqueio: 2 };

// Espelha _estado_regra do travas.py: 'global', 'bloqueio' e o legado '1' so
// valem bloqueio se o modo de operacao do sistema estiver em bloqueio.
const resolverEstadoCom = (modo) => (valor) => {
  const v = String(valor ?? '').trim().toLowerCase();
  if (v === 'log') return 'log';
  if (v === 'global' || v === 'bloqueio' || v === '1') {
    return modo === 'bloqueio' ? 'bloqueio' : 'log';
  }
  return 'off';
};

// Regras cuja chave e a mesma na requisicao e na ordem de producao.
const REGRAS_COMPARTILHADAS = [
  { chave: 'regra_forma_farmaceutica', rotulo: 'Forma farmacêutica × etapa' },
  { chave: 'regra_etapa_incompativel', rotulo: 'Ordem entre etapas' },
  { chave: 'regra_setor_autorizado', rotulo: 'Setor autorizado' },
  { chave: 'regra_a_bloqueio_pos_final', rotulo: 'Produção após etapa final' },
  { chave: 'regra_producao_pos_conferencia', rotulo: 'Produção após conferência ou logística' }
];

const ORIGENS_LOG = [
  { valor: '', rotulo: 'Todas' },
  { valor: 'requisicao', rotulo: 'Requisição' },
  { valor: 'ordem_producao', rotulo: 'Ordem de Produção' }
];

const ESTADOS_REGRA = [
  { valor: 'off',    rotulo: 'Desligada',           ajuda: 'Nem avalia.' },
  { valor: 'log',    rotulo: 'Só registrar',        ajuda: 'Avalia e grava nos Logs, mas deixa o bipe passar.' },
  { valor: 'global', rotulo: 'Seguir o modo global', ajuda: 'Obedece o botão BLOQUEIO/ALTERNAR do topo.' }
];

const Configuracoes = () => {
  const { api, user } = useAuth();
  const [aba, setAba] = useState(ABA.ETAPA);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const podeEditar = user?.nivel >= 3;

  // ---- dados
  const [parametros, setParametros] = useState([]);
  const [etapas, setEtapas] = useState([]);
  const [vinculos, setVinculos] = useState([]);
  const [terminais, setTerminais] = useState([]);
  const [descobertos, setDescobertos] = useState([]);
  const [logs, setLogs] = useState([]);
  const [incompativeis, setIncompativeis] = useState([]);
  const [formasRegras, setFormasRegras] = useState([]);
  const [formasDisponiveis, setFormasDisponiveis] = useState([]);
  const [simulacao, setSimulacao] = useState(null);
  const [simulando, setSimulando] = useState(false);
  const [resumo, setResumo] = useState(null);

  // ---- dialogs
  const [etapaEdit, setEtapaEdit] = useState(null);
  const [vinculoEdit, setVinculoEdit] = useState(null);
  const [terminalEdit, setTerminalEdit] = useState(null);
  const [regraEdit, setRegraEdit] = useState(null);
  const [formaEdit, setFormaEdit] = useState(null);
  const [salvando, setSalvando] = useState(false);

  // ---- filtros
  const [busca, setBusca] = useState('');
  // Dois estados: o que está sendo digitado e o que foi efetivamente aplicado.
  // Sem essa separação, cada tecla dispara a consulta, o card desmonta enquanto
  // carrega e o input perde o foco a cada caractere.
  const [filtroLog, setFiltroLog] = useState({
    data_inicio: '', data_fim: '', tipo: '', codigo_funcionario: '', unidade: '', origem: ''
  });
  const [filtroAplicado, setFiltroAplicado] = useState({
    data_inicio: '', data_fim: '', tipo: '', codigo_funcionario: '', unidade: '', origem: ''
  });
  const [carregandoLogs, setCarregandoLogs] = useState(false);
  const [subAba, setSubAba] = useState(SUBABA.REQUISICAO);

  const param = (chave) => parametros.find(p => p.chave === chave);
  const paramBool = (chave) => param(chave)?.valor === '1';
  const modoOperacao = param('modo_operacao')?.valor || 'log';
  const resolverEstado = resolverEstadoCom(modoOperacao);

  const carregar = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [p, e, v, t] = await Promise.all([
        api.listarParametros(),
        api.listarEtapas(),
        api.listarFuncionarioSetores(),
        api.listarTerminais()
      ]);
      if (p.success) setParametros(p.dados || []);
      if (e.success) setEtapas(e.dados || []);
      if (v.success) setVinculos(v.dados || []);
      if (t.success) setTerminais(t.dados || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { carregar(); }, [carregar]);

  // Usa `carregandoLogs` (não `loading`) para não desmontar o card de filtros.
  const carregarLogs = useCallback(async () => {
    setCarregandoLogs(true); setError('');
    try {
      const [l, r] = await Promise.all([
        api.listarLogsAuditoria(filtroAplicado),
        api.resumoLogsAuditoria({
          data_inicio: filtroAplicado.data_inicio, data_fim: filtroAplicado.data_fim,
          origem: filtroAplicado.origem
        })
      ]);
      if (l.success) setLogs(l.dados || []);
      if (r.success) setResumo(r.dados || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setCarregandoLogs(false);
    }
  }, [api, filtroAplicado]);

  const carregarDescobertos = useCallback(async () => {
    try {
      const d = await api.listarTerminaisDescobertos();
      if (d.success) setDescobertos(d.dados || []);
    } catch (err) { /* silencioso: aba ainda utilizável sem isso */ }
  }, [api]);

  const carregarIncompativeis = useCallback(async () => {
    try {
      const [i, f, d] = await Promise.all([
        api.listarEtapasIncompativeis(),
        api.listarFormasIncompativeis(),
        api.listarFormasFarmaceuticas()
      ]);
      if (i.success) setIncompativeis(i.dados || []);
      if (f.success) setFormasRegras(f.dados || []);
      if (d.success) setFormasDisponiveis(d.dados || []);
    } catch (err) { /* silencioso: demais abas seguem utilizáveis sem isso */ }
  }, [api]);

  useEffect(() => {
    if (aba === ABA.LOGS) carregarLogs();
    if (aba === ABA.TRAVA_IP) carregarDescobertos();
    if (aba === ABA.PARAMETROS) carregarIncompativeis();
  }, [aba, carregarLogs, carregarDescobertos, carregarIncompativeis]);

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };

  const alternarParametro = async (chave, valorAtual) => {
    if (!podeEditar) return;
    try {
      const novo = valorAtual === '1' ? '0' : '1';
      const res = await api.atualizarParametro(chave, novo);
      if (!res.success) throw new Error(res.message);
      setParametros(prev => prev.map(p => p.chave === chave ? { ...p, valor: novo } : p));
      flash('Configuração salva');
    } catch (err) { setError(err.message); }
  };

  const trocarModo = async (novoModo) => {
    if (!podeEditar) return;
    // Virar para bloqueio afeta o chao de fabrica inteiro na hora — pedir
    // confirmacao explicita, como ja e feito para remover um terminal.
    if (novoModo === 'bloqueio' && !window.confirm(
      'Ativar modo BLOQUEIO?\n\n'
      + 'A partir de agora as bipagens que violarem as regras serão RECUSADAS '
      + 'de verdade, em todos os terminais.\n\n'
      + 'Confirme apenas se você já revisou a aba Logs e ninguém fora dos '
      + 'casos investigados aparece com recusas.')) return;
    try {
      const res = await api.atualizarParametro('modo_operacao', novoModo);
      if (!res.success) throw new Error(res.message);
      setParametros(prev => prev.map(p => p.chave === 'modo_operacao' ? { ...p, valor: novoModo } : p));
      flash(`Modo alterado para ${novoModo}`);
    } catch (err) { setError(err.message); }
  };

  const salvarEtapa = async () => {
    setSalvando(true); setError('');
    try {
      const res = await api.atualizarEtapa(etapaEdit.id, {
        etapa: etapaEdit.etapa, tipo: etapaEdit.tipo,
        unidade: etapaEdit.unidade, ordem: etapaEdit.ordem, ativo: etapaEdit.ativo ? 1 : 0
      });
      if (!res.success) throw new Error(res.message);
      setEtapaEdit(null); flash('Classificação salva'); carregar();
    } catch (err) { setError(err.message); } finally { setSalvando(false); }
  };

  const salvarVinculo = async () => {
    setSalvando(true); setError('');
    try {
      const res = await api.atualizarFuncionarioSetores(vinculoEdit.codigo, vinculoEdit.setoresSelecionados);
      if (!res.success) throw new Error(res.message);
      setVinculoEdit(null); flash('Setores atualizados'); carregar();
    } catch (err) { setError(err.message); } finally { setSalvando(false); }
  };

  const salvarTerminal = async () => {
    setSalvando(true); setError('');
    try {
      const dados = {
        ip: terminalEdit.ip, nome_terminal: terminalEdit.nome_terminal,
        unidade: terminalEdit.unidade, ativo: terminalEdit.ativo ? 1 : 0
      };
      const res = terminalEdit.id
        ? await api.atualizarTerminal(terminalEdit.id, dados)
        : await api.criarTerminal(dados);
      if (!res.success) throw new Error(res.message);
      setTerminalEdit(null); flash('Terminal salvo'); carregar(); carregarDescobertos();
    } catch (err) { setError(err.message); } finally { setSalvando(false); }
  };

  const removerTerminal = async (id) => {
    if (!window.confirm('Remover este terminal?')) return;
    try {
      const res = await api.excluirTerminal(id);
      if (!res.success) throw new Error(res.message);
      flash('Terminal removido'); carregar();
    } catch (err) { setError(err.message); }
  };

  // As travas novas gravam 'off' | 'log' | 'global', e nao '1'/'0' como as
  // antigas — por isso um setter proprio em vez de alternarParametro.
  const trocarEstadoRegra = async (chave, valor) => {
    if (!podeEditar) return;
    try {
      const res = await api.atualizarParametro(chave, valor);
      if (!res.success) throw new Error(res.message);
      setParametros(prev => prev.map(p => p.chave === chave ? { ...p, valor } : p));
      flash('Configuração salva');
    } catch (err) { setError(err.message); }
  };

  const salvarRegraEtapa = async () => {
    setSalvando(true); setError('');
    try {
      const dados = {
        etapa_gatilho: regraEdit.etapa_gatilho,
        bloqueadas: regraEdit.bloqueadas,
        ativo: regraEdit.ativo ? 1 : 0,
        motivo: regraEdit.motivo || null
      };
      const res = regraEdit.novo
        ? await api.criarEtapaIncompativel(dados)
        : await api.atualizarEtapaIncompativel(regraEdit.etapa_gatilho, dados);
      if (!res.success) throw new Error(res.message);
      setRegraEdit(null); flash('Regra salva'); carregarIncompativeis();
    } catch (err) { setError(err.message); } finally { setSalvando(false); }
  };

  const removerRegraEtapa = async (gatilho, total) => {
    if (!window.confirm(`Remover a regra do gatilho ${gatilho}?\n\n`
      + `As ${total} combinações serão apagadas.`)) return;
    try {
      const res = await api.excluirEtapaIncompativel(gatilho);
      if (!res.success) throw new Error(res.message);
      flash('Regra removida'); carregarIncompativeis();
    } catch (err) { setError(err.message); }
  };

  const alternarRegraEtapa = async (gatilho, ativo, linha) => {
    if (!podeEditar) return;
    if (linha && parcial(linha, 'total_bloqueadas') && !window.confirm(
      `Este gatilho está misto: ${linha.ativos} de ${linha.total_bloqueadas} etapas ativas`
      + ` (desligadas: ${linha.inativas}).\n\n`
      + `Continuar deixa TODAS no mesmo estado e perde essa exceção.`)) return;
    try {
      const res = await api.alternarEtapaIncompativel(gatilho, ativo ? 0 : 1);
      if (!res.success) throw new Error(res.message);
      setIncompativeis(prev => prev.map(r => r.etapa_gatilho === gatilho
        ? { ...r, ativo: ativo ? 0 : 1 } : r));
      flash('Regra atualizada');
    } catch (err) { setError(err.message); }
  };

  const salvarRegraForma = async () => {
    setSalvando(true); setError('');
    try {
      const dados = {
        tpformafarma: formaEdit.tpformafarma,
        forma_nome: formaEdit.forma_nome || null,
        etapas: formaEdit.etapas,
        ativo: formaEdit.ativo ? 1 : 0,
        motivo: formaEdit.motivo || null
      };
      const res = formaEdit.novo
        ? await api.criarFormaIncompativel(dados)
        : await api.atualizarFormaIncompativel(formaEdit.tpformafarma, dados);
      if (!res.success) throw new Error(res.message);
      setFormaEdit(null); flash('Regra salva'); carregarIncompativeis();
    } catch (err) { setError(err.message); } finally { setSalvando(false); }
  };

  const removerRegraForma = async (tpf, nome, total) => {
    if (!window.confirm(`Remover a regra da forma ${nome || tpf}?\n\n`
      + `As ${total} etapas bloqueadas serão apagadas.`)) return;
    try {
      const res = await api.excluirFormaIncompativel(tpf);
      if (!res.success) throw new Error(res.message);
      flash('Regra removida'); carregarIncompativeis();
    } catch (err) { setError(err.message); }
  };

  const alternarRegraForma = async (tpf, ativo, linha) => {
    if (!podeEditar) return;
    if (linha && parcial(linha, 'total_etapas') && !window.confirm(
      `Esta forma está mista: ${linha.ativos} de ${linha.total_etapas} etapas ativas`
      + ` (desligadas: ${linha.inativas}).\n\n`
      + `Continuar deixa TODAS no mesmo estado e perde essa exceção.`)) return;
    try {
      const res = await api.alternarFormaIncompativel(tpf, ativo ? 0 : 1);
      if (!res.success) throw new Error(res.message);
      setFormasRegras(prev => prev.map(r => r.tpformafarma === tpf
        ? { ...r, ativo: ativo ? 0 : 1 } : r));
      flash('Regra atualizada');
    } catch (err) { setError(err.message); }
  };

  // Varre REQUISICOES inteira, entao fica atras de um botao em vez de rodar no
  // carregamento da aba.
  const medirImpacto = async () => {
    setSimulando(true); setError('');
    try {
      const res = await api.simularIncompativeis(6);
      if (!res.success) throw new Error(res.message);
      setSimulacao(res.dados);
    } catch (err) { setError(err.message); } finally { setSimulando(false); }
  };

  const exportarLogs = () => {
    const head = ['data', 'origem', 'tipo', 'funcionario', 'codigo', 'requisicao', 'setor', 'etapa', 'unidade', 'motivo', 'modo', 'ip'];
    const linhas = logs.map(l => [
      l.data, l.origem, l.tipo, l.nome_funcionario, l.codigo_funcionario, l.codigo_requisicao,
      l.setor, l.etapa, l.unidade, l.motivo, l.modo, l.ip_origem
    ].map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(';'));
    const blob = new Blob(['﻿' + [head.join(';'), ...linhas].join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `auditoria_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const setoresDisponiveis = etapas.map(e => e.setor).sort();

  // Etapas canonicas para os selects da aba Parametros. Deriva de SETOR_ETAPA
  // (via `etapas`, ja carregado) e faz uniao com as que as regras salvas ja
  // referenciam: se uma etapa for renomeada na aba Etapa, a regra antiga
  // continua visivel e editavel em vez de sumir do select e ser apagada no
  // proximo salvamento.
  const etapasDisponiveis = [...new Set([
    ...etapas.filter(e => e.ativo === 1 || e.ativo === true).map(e => e.etapa),
    ...incompativeis.flatMap(r => [r.etapa_gatilho, ...String(r.bloqueadas || '').split(', ')]),
    ...formasRegras.flatMap(r => String(r.etapas || '').split(', '))
  ])].filter(Boolean).sort();

  // O gatilho e a chave do recurso: oferecer um que ja tem regra so produziria
  // um 409 na cara do usuario.
  const gatilhosLivres = etapasDisponiveis
    .filter(e => !incompativeis.some(r => r.etapa_gatilho === e));
  const formasLivres = formasDisponiveis
    .filter(f => !formasRegras.some(r => r.tpformafarma === f.tpformafarma));

  const estadoRegra = (chave) => param(chave)?.valor || 'off';

  // Espelha _estado_op do travas.py: o efetivo na OP e o MENOR entre o mestre
  // e a chave, com as duas RESOLVIDAS antes de comparar. Sem resolver, uma
  // regra em 'global' com o sistema em modo log apareceria como 'global'
  // enquanto o motor a trata como 'log' — e as chaves legadas ('1'/'0', que e
  // como setor autorizado e pos-final estao gravadas) nem entravam na conta.
  const efetivoNaOp = (chave) => {
    const mestre = resolverEstado(estadoRegra('regra_op_travas'));
    const regra = resolverEstado(estadoRegra(chave));
    return ORDEM_ESTADO[mestre] < ORDEM_ESTADO[regra] ? mestre : regra;
  };

  // Um grupo pode estar misto: no seed, a Capsula bloqueia xarope e sache mas
  // NAO cosmetico. Sem mostrar isso, o switch mentiria e o salvar apagaria a
  // excecao sem avisar.
  const parcial = (r, campoTotal) => r.ativos > 0 && r.ativos < r[campoTotal];
  const chipInativo = (r, etapa) => String(r.inativas || '')
    .split(', ').filter(Boolean).includes(etapa);
  const simulTravaA = (tpf, etapa) => simulacao?.trava_a
    ?.find(x => x.tpformafarma === tpf && x.etapa === etapa)?.bipes_recusados;
  const simulTravaB = (gatilho) => {
    if (!simulacao?.trava_b) return null;
    return simulacao.trava_b
      .filter(x => x.etapa_gatilho === gatilho)
      .reduce((soma, x) => soma + (x.bipes_recusados || 0), 0);
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <Paper elevation={0} sx={{
        p: 3, mb: 3, borderRadius: 2,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white'
      }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>⚙️ Configuração</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Travas antifraude do Sistema de Requisição
            </Typography>
          </Box>
          <Box textAlign="right">
            <Typography variant="caption" sx={{ opacity: 0.9, display: 'block', mb: 0.5 }}>
              Modo de operação
            </Typography>
            <Chip
              label={modoOperacao === 'bloqueio' ? '🔒 BLOQUEIO' : '📝 LOG (não bloqueia)'}
              sx={{
                bgcolor: modoOperacao === 'bloqueio' ? '#ef4444' : '#f59e0b',
                color: 'white', fontWeight: 'bold'
              }}
            />
            {podeEditar && (
              <Button
                size="small" variant="outlined"
                sx={{ ml: 1, color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}
                onClick={() => trocarModo(modoOperacao === 'bloqueio' ? 'log' : 'bloqueio')}
              >
                Alternar
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {modoOperacao === 'log' && (
        <Alert severity="info" icon={<WarningIcon />} sx={{ mb: 3 }}>
          <b>Modo LOG ativo.</b> As regras estão avaliando e registrando na aba Logs, mas
          <b> ninguém é bloqueado</b>. Revise as recusas simuladas antes de alternar para BLOQUEIO.
        </Alert>
      )}

      <Card elevation={0} sx={{ borderRadius: 2, mb: 3 }}>
        <Tabs value={aba} onChange={(e, v) => setAba(v)} variant="scrollable" scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Etapa" />
          <Tab label="Parâmetros" />
          <Tab label="Atrelar Setor" />
          <Tab label="Trava por IP" />
          <Tab label="Logs" />
          <Tab label="Funcionários" />
          <Tab label="Setores" />
          <Tab label="Usuários" />
        </Tabs>
      </Card>

      {loading && (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      )}

      {/* ================= ABA 1 — ETAPA ================= */}
      {!loading && aba === ABA.ETAPA && (
        <>
          <Card elevation={0} sx={{ borderRadius: 2, mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>Regras ativas</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Cada regra liga e desliga de forma independente.
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={paramBool('regra_a_bloqueio_pos_final')}
                    disabled={!podeEditar}
                    onChange={() => alternarParametro('regra_a_bloqueio_pos_final', param('regra_a_bloqueio_pos_final')?.valor)}
                  />
                }
                label={
                  <Box>
                    <Typography fontWeight="600">
                      Regra A — Requisição em etapa final não pode ser bipada pela produção
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Sem prazo: ficha parada em produção não é afetada, mesmo que demore dias.
                      Só bloqueia depois que a requisição atinge etapa final.
                    </Typography>
                  </Box>
                }
                sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={paramBool('regra_b_exige_visto')}
                    disabled={!podeEditar}
                    onChange={() => alternarParametro('regra_b_exige_visto', param('regra_b_exige_visto')?.valor)}
                  />
                }
                label={
                  <Box>
                    <Typography fontWeight="600">
                      Regra B — Requisição sem visto farmacêutico não pode ser bipada pela produção
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ⚠️ Hoje 2,8% da produção ocorre sem visto registrado (~51 recusas/dia).
                      Manter desligada até a cobertura do visto melhorar.
                    </Typography>
                  </Box>
                }
                sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}
              />

              <Divider sx={{ my: 2 }} />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={paramBool('regra_etapa_duplicada')}
                    disabled={!podeEditar}
                    onChange={() => alternarParametro('regra_etapa_duplicada', param('regra_etapa_duplicada')?.valor)}
                  />
                }
                label={
                  <Box>
                    <Typography fontWeight="600">Mesma etapa não pode ser bipada duas vezes</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Compara pela etapa canônica: "Pesagem" e "PESAGEM LAB CABO FRIO" contam como a mesma.
                    </Typography>
                  </Box>
                }
                sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={paramBool('regra_setor_autorizado')}
                    disabled={!podeEditar}
                    onChange={() => alternarParametro('regra_setor_autorizado', param('regra_setor_autorizado')?.valor)}
                  />
                }
                label={
                  <Box>
                    <Typography fontWeight="600">Funcionário só bipa em setores liberados</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Configurado na aba "Atrelar Setor".
                    </Typography>
                  </Box>
                }
                sx={{ display: 'flex', alignItems: 'flex-start' }}
              />
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight="bold">Classificação dos setores</Typography>
                <IconButton onClick={carregar}><RefreshIcon /></IconButton>
              </Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <b>Logística</b> é transporte entre unidades (a produção continua no destino).
                <b> Entrega final</b> encerra o fluxo. Classificar expedição como final bloquearia
                quem produz legitimamente na filial de destino.
              </Alert>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Setor</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Etapa</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Unidade</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="right">Ordem</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="right">Bipes 3m</TableCell>
                      {podeEditar && <TableCell align="right">Ações</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {etapas.map(e => (
                      <TableRow key={e.id} hover>
                        <TableCell>{e.setor}</TableCell>
                        <TableCell><Chip label={e.etapa} size="small" variant="outlined" /></TableCell>
                        <TableCell>
                          <Chip label={e.tipo} size="small"
                                sx={{ bgcolor: CORES_TIPO[e.tipo] || '#94a3b8', color: 'white' }} />
                        </TableCell>
                        <TableCell>{e.unidade}</TableCell>
                        <TableCell align="right">{e.ordem}</TableCell>
                        <TableCell align="right">{(e.bipagens_3m || 0).toLocaleString('pt-BR')}</TableCell>
                        {podeEditar && (
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => setEtapaEdit({ ...e, ativo: e.ativo === 1 || e.ativo === true })}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}

      {/* ================= ABA 2 — PARÂMETROS ================= */}
      {!loading && aba === ABA.PARAMETROS && (
        <>
          <Card elevation={0} sx={{ borderRadius: 2, mb: 3 }}>
            <Tabs value={subAba} onChange={(e, v) => setSubAba(v)}
                  sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tab label="Requisição" />
              <Tab label="Ordem de Produção" />
            </Tabs>
          </Card>
        </>
      )}

      {/* ---- Parâmetros / Requisição ---- */}
      {!loading && aba === ABA.PARAMETROS && subAba === SUBABA.REQUISICAO && (
        <>
          <Card elevation={0} sx={{ borderRadius: 2, mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Travas de forma farmacêutica
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Duas travas independentes. Cada uma tem três estados porque o sistema já está
                em modo bloqueio: "Só registrar" permite medir o impacto real antes de começar
                a recusar bipes.
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography fontWeight="600" gutterBottom>
                    Forma farmacêutica × etapa
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    Compara a forma oficial da fórmula (vinda do Firebird) com a etapa que está
                    sendo bipada. Recusa o bipe errado no momento em que ele acontece — uma
                    fórmula que é cápsula não tem o que fazer no cosmético.
                  </Typography>
                  <FormControl fullWidth size="small">
                    <InputLabel>Estado da trava</InputLabel>
                    <Select
                      value={estadoRegra('regra_forma_farmaceutica')}
                      label="Estado da trava"
                      disabled={!podeEditar}
                      onChange={(e) => trocarEstadoRegra('regra_forma_farmaceutica', e.target.value)}
                    >
                      {ESTADOS_REGRA.map(o => (
                        <MenuItem key={o.valor} value={o.valor}>
                          {o.rotulo} — <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>{o.ajuda}</Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {estadoRegra('regra_forma_farmaceutica') !== 'off'
                    && !formasRegras.some(r => r.ativo) && (
                    <Alert severity="warning" sx={{ mt: 1.5 }}>
                      Nenhuma forma ativa na tabela abaixo — a trava está ligada mas não bloqueia nada.
                    </Alert>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography fontWeight="600" gutterBottom>
                    Ordem entre etapas
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    Se a requisição já foi bipada em cosmético, xarope ou sachê, bipes posteriores
                    em pesagem, encapsulação ou trituração são recusados. A ordem inversa
                    (manipular e depois envasar) continua liberada.
                  </Typography>
                  <FormControl fullWidth size="small">
                    <InputLabel>Estado da trava</InputLabel>
                    <Select
                      value={estadoRegra('regra_etapa_incompativel')}
                      label="Estado da trava"
                      disabled={!podeEditar}
                      onChange={(e) => trocarEstadoRegra('regra_etapa_incompativel', e.target.value)}
                    >
                      {ESTADOS_REGRA.map(o => (
                        <MenuItem key={o.valor} value={o.valor}>
                          {o.rotulo} — <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>{o.ajuda}</Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {estadoRegra('regra_etapa_incompativel') !== 'off'
                    && !incompativeis.some(r => r.ativo) && (
                    <Alert severity="warning" sx={{ mt: 1.5 }}>
                      Nenhum gatilho ativo na tabela abaixo — a trava está ligada mas não bloqueia nada.
                    </Alert>
                  )}
                </Grid>
              </Grid>

              <Grid container spacing={3} sx={{ mt: 0 }}>
                <Grid item xs={12} md={6}>
                  <Typography fontWeight="600" gutterBottom>
                    Produção após conferência ou logística
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    Depois que a ficha passou pela conferência, pelo peso médio, pela liberação ou
                    entrou na logística, ela não volta para a produção. Não tem lógica pesagem
                    depois que foi para o peso médio. Complementa a "produção após etapa final",
                    que só cobre a entrega e a logística de outra unidade.
                  </Typography>
                  <FormControl fullWidth size="small">
                    <InputLabel>Estado da trava</InputLabel>
                    <Select
                      value={estadoRegra('regra_producao_pos_conferencia')}
                      label="Estado da trava"
                      disabled={!podeEditar}
                      onChange={(e) => trocarEstadoRegra('regra_producao_pos_conferencia', e.target.value)}
                    >
                      {ESTADOS_REGRA.map(o => (
                        <MenuItem key={o.valor} value={o.valor}>
                          {o.rotulo} — <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>{o.ajuda}</Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Box display="flex" alignItems="center" gap={2} mt={3} flexWrap="wrap">
                <Button variant="outlined" size="small" onClick={medirImpacto} disabled={simulando}
                  startIcon={simulando ? <CircularProgress size={16} /> : <SearchIcon />}>
                  {simulando ? 'Medindo...' : 'Medir impacto (6 meses)'}
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Conta quantos bipes cada regra teria recusado no histórico. Leva alguns segundos.
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* ---------- Trava A: forma farmacêutica x etapa ---------- */}
          <Card elevation={0} sx={{ borderRadius: 2, mb: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2} flexWrap="wrap">
                <Box>
                  <Typography variant="h6" fontWeight="bold">Forma farmacêutica × etapa</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Uma linha por forma. As etapas listadas nunca podem receber bipe dessa forma.
                  </Typography>
                </Box>
                <Box display="flex" gap={1}>
                  <IconButton onClick={carregarIncompativeis}><RefreshIcon /></IconButton>
                  {podeEditar && (
                    <Button variant="contained" startIcon={<AddIcon />} disabled={!formasLivres.length}
                      onClick={() => setFormaEdit({
                        novo: true, tpformafarma: '', forma_nome: '',
                        etapas: [], motivo: '', ativo: true
                      })}>
                      Nova forma
                    </Button>
                  )}
                </Box>
              </Box>

              {formasRegras.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <WarningIcon sx={{ fontSize: 60, color: '#e2e8f0' }} />
                  <Typography color="text.secondary">Nenhuma forma configurada</Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#f8fafc' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Forma</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Não pode bipar em</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Bipes/6m</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Ativo</TableCell>
                        {podeEditar && <TableCell sx={{ fontWeight: 'bold' }} align="right">Ações</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formasRegras.map(r => (
                        <TableRow key={r.tpformafarma} hover>
                          <TableCell>
                            <Chip size="small" label={r.forma_nome || `Forma ${r.tpformafarma}`} />
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              #{r.tpformafarma}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {String(r.etapas || '').split(', ').filter(Boolean).map(e => (
                              <Chip key={e} size="small" variant="outlined" label={e}
                                sx={{ mr: 0.5, mb: 0.5, opacity: chipInativo(r, e) ? 0.4 : 1 }}
                                color={String(r.orfas || '').includes(e) ? 'warning' : 'default'} />
                            ))}
                            {r.orfas && (
                              <Tooltip title="Etapa que não existe mais em SETOR_ETAPA — essa parte da regra nunca vai disparar. Revise na aba Etapa.">
                                <WarningIcon fontSize="small" color="warning" sx={{ verticalAlign: 'middle' }} />
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {String(r.etapas || '').split(', ').filter(Boolean)
                              .map(e => simulTravaA(r.tpformafarma, e))
                              .filter(v => v !== undefined && v !== null)
                              .reduce((a, b) => a + b, 0) || (simulacao ? 0 : '—')}
                          </TableCell>
                          <TableCell>
                            <Switch size="small" checked={!!r.ativo} disabled={!podeEditar}
                              onChange={() => alternarRegraForma(r.tpformafarma, r.ativo, r)} />
                            {parcial(r, 'total_etapas') && (
                              <Typography variant="caption" color="warning.main" display="block">
                                {r.ativos} de {r.total_etapas}
                              </Typography>
                            )}
                          </TableCell>
                          {podeEditar && (
                            <TableCell align="right">
                              <IconButton size="small" onClick={() => setFormaEdit({
                                novo: false, tpformafarma: r.tpformafarma,
                                forma_nome: r.forma_nome || '',
                                etapas: String(r.etapas || '').split(', ').filter(Boolean),
                                motivo: r.motivo || '', ativo: !!r.ativo
                              })}><EditIcon fontSize="small" /></IconButton>
                              <IconButton size="small" color="error"
                                onClick={() => removerRegraForma(r.tpformafarma, r.forma_nome, r.total_etapas)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* ---------- Trava B: ordem entre etapas ---------- */}
          <Card elevation={0} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2} flexWrap="wrap">
                <Box>
                  <Typography variant="h6" fontWeight="bold">Ordem entre etapas</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Uma linha por gatilho. Bipar o gatilho passa a impedir as etapas listadas —
                    a ordem inversa continua liberada.
                  </Typography>
                </Box>
                <Box display="flex" gap={1}>
                  <IconButton onClick={carregarIncompativeis}><RefreshIcon /></IconButton>
                  {podeEditar && (
                    <Button variant="contained" startIcon={<AddIcon />} disabled={!gatilhosLivres.length}
                      onClick={() => setRegraEdit({
                        novo: true, etapa_gatilho: '', bloqueadas: [], motivo: '', ativo: true
                      })}>
                      Nova regra
                    </Button>
                  )}
                </Box>
              </Box>

              {incompativeis.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <WarningIcon sx={{ fontSize: 60, color: '#e2e8f0' }} />
                  <Typography color="text.secondary">Nenhuma regra configurada</Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#f8fafc' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Etapa gatilho</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Bloqueia depois</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Bipes/6m</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Ativo</TableCell>
                        {podeEditar && <TableCell sx={{ fontWeight: 'bold' }} align="right">Ações</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {incompativeis.map(r => (
                        <TableRow key={r.etapa_gatilho} hover>
                          <TableCell><Chip size="small" label={r.etapa_gatilho} /></TableCell>
                          <TableCell>
                            {String(r.bloqueadas || '').split(', ').filter(Boolean).map(b => (
                              <Chip key={b} size="small" variant="outlined" label={b}
                                sx={{ mr: 0.5, mb: 0.5, opacity: chipInativo(r, b) ? 0.4 : 1 }}
                                color={String(r.orfas || '').includes(b) ? 'warning' : 'default'} />
                            ))}
                            {r.orfas && (
                              <Tooltip title="Etapa que não existe mais em SETOR_ETAPA — essa parte da regra nunca vai disparar. Revise na aba Etapa.">
                                <WarningIcon fontSize="small" color="warning" sx={{ verticalAlign: 'middle' }} />
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {simulTravaB(r.etapa_gatilho) ?? '—'}
                          </TableCell>
                          <TableCell>
                            <Switch size="small" checked={!!r.ativo} disabled={!podeEditar}
                              onChange={() => alternarRegraEtapa(r.etapa_gatilho, r.ativo, r)} />
                            {parcial(r, 'total_bloqueadas') && (
                              <Typography variant="caption" color="warning.main" display="block">
                                {r.ativos} de {r.total_bloqueadas}
                              </Typography>
                            )}
                          </TableCell>
                          {podeEditar && (
                            <TableCell align="right">
                              <IconButton size="small" onClick={() => setRegraEdit({
                                novo: false, etapa_gatilho: r.etapa_gatilho,
                                bloqueadas: String(r.bloqueadas || '').split(', ').filter(Boolean),
                                motivo: r.motivo || '', ativo: !!r.ativo
                              })}><EditIcon fontSize="small" /></IconButton>
                              <IconButton size="small" color="error"
                                onClick={() => removerRegraEtapa(r.etapa_gatilho, r.total_bloqueadas)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* ---- Parâmetros / Ordem de Produção ---- */}
      {!loading && aba === ABA.PARAMETROS && subAba === SUBABA.ORDEM_PRODUCAO && (
        <>
          <Card elevation={0} sx={{ borderRadius: 2, mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Travas da leitura de Ordem de Produção
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Até agora a leitura de OP entrava direto no banco, sem checagem e sem deixar
                rastro. O interruptor abaixo controla se ela passa a ser avaliada.
              </Typography>

              {!param('regra_op_travas') && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  As chaves desta aba ainda não existem no banco. Os controles abaixo mostram
                  "Desligada" mas <strong>não salvam</strong> — a API só atualiza chave existente.
                  Rode <code>node backend/scripts/add-trava-op.js</code> no servidor e recarregue.
                </Alert>
              )}

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography fontWeight="600" gutterBottom>
                    Travas de OP (interruptor mestre)
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    Vale como teto: as regras compartilhadas nunca pesam mais na OP do que este
                    controle. É o que permite medir na ordem de produção sem afrouxar a
                    requisição, já que as duas usam as mesmas chaves.
                  </Typography>
                  <FormControl fullWidth size="small">
                    <InputLabel>Estado</InputLabel>
                    <Select
                      value={estadoRegra('regra_op_travas')}
                      label="Estado"
                      disabled={!podeEditar}
                      onChange={(e) => trocarEstadoRegra('regra_op_travas', e.target.value)}
                    >
                      {ESTADOS_REGRA.map(o => (
                        <MenuItem key={o.valor} value={o.valor}>
                          {o.rotulo} — <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>{o.ajuda}</Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {estadoRegra('regra_op_travas') === 'off' && (
                    <Alert severity="info" sx={{ mt: 1.5 }}>
                      Com o mestre desligado, a leitura de OP não é avaliada por nenhuma trava —
                      nem a de lote.
                    </Alert>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography fontWeight="600" gutterBottom>
                    Lote repetido no setor
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    O mesmo setor não pode bipar a mesma ordem duas vezes. Como o FórmulaCerta
                    gera um lote novo a cada emissão, a ordem é o lote. Esta regra é exclusiva da
                    OP e não é limitada pelo mestre — só exige que ele não esteja desligado.
                  </Typography>
                  <FormControl fullWidth size="small">
                    <InputLabel>Estado</InputLabel>
                    <Select
                      value={estadoRegra('regra_lote_duplicado')}
                      label="Estado"
                      disabled={!podeEditar}
                      onChange={(e) => trocarEstadoRegra('regra_lote_duplicado', e.target.value)}
                    >
                      {ESTADOS_REGRA.map(o => (
                        <MenuItem key={o.valor} value={o.valor}>
                          {o.rotulo} — <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>{o.ajuda}</Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Regras compartilhadas com a Requisição
              </Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Estas usam a <strong>mesma chave</strong> nos dois mundos: mudar aqui muda também
                na aba Requisição. Na ordem de produção elas ainda são limitadas pelo mestre acima.
              </Alert>
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#f8fafc' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Regra</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Estado da chave</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Efetivo na OP</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {REGRAS_COMPARTILHADAS.map(rc => (
                      <TableRow key={rc.chave} hover>
                        <TableCell>
                          {rc.rotulo}
                          <Typography variant="caption" color="text.secondary" display="block">
                            {rc.chave}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={estadoRegra(rc.chave)} />
                          {resolverEstado(estadoRegra(rc.chave)) !== estadoRegra(rc.chave) && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              vale {resolverEstado(estadoRegra(rc.chave))}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" color="primary" variant="outlined"
                            label={efetivoNaOp(rc.chave)} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Terminal/IP, etapa duplicada e visto farmacêutico não valem para ordem de
                produção — o equivalente da etapa duplicada aqui é a regra de lote.
              </Typography>
            </CardContent>
          </Card>
        </>
      )}

      {/* ================= ABA 3 — ATRELAR SETOR ================= */}
      {!loading && aba === ABA.ATRELAR_SETOR && (
        <Card elevation={0} sx={{ borderRadius: 2 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2} flexWrap="wrap">
              <Typography variant="h6" fontWeight="bold">Setores liberados por funcionário</Typography>
              <TextField
                size="small" placeholder="Buscar funcionário..."
                value={busca} onChange={(e) => setBusca(e.target.value)}
                InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: '#64748b' }} /> }}
              />
            </Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              No terminal, o funcionário só enxerga os setores liberados aqui — trocar de setor
              deixa de ser uma forma de contornar as travas.
            </Alert>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Funcionário</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Setores liberados</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Qtd</TableCell>
                    {podeEditar && <TableCell align="right">Ações</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vinculos
                    .filter(v => !busca || String(v.nome || '').toLowerCase().includes(busca.toLowerCase())
                                        || String(v.codigo || '').includes(busca))
                    .map(v => (
                      <TableRow key={v.codigo} hover>
                        <TableCell>{v.nome}</TableCell>
                        <TableCell>{v.codigo}</TableCell>
                        <TableCell sx={{ maxWidth: 420 }}>
                          {v.total_setores > 0
                            ? <Typography variant="caption">{v.setores}</Typography>
                            : <Chip label="nenhum setor liberado" size="small" color="warning" />}
                        </TableCell>
                        <TableCell align="right">{v.total_setores}</TableCell>
                        {podeEditar && (
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => setVinculoEdit({
                              codigo: v.codigo, nome: v.nome,
                              setoresSelecionados: v.setores ? v.setores.split(', ') : []
                            })}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* ================= ABA 4 — TRAVA POR IP ================= */}
      {!loading && aba === ABA.TRAVA_IP && (
        <>
          <Card elevation={0} sx={{ borderRadius: 2, mb: 3 }}>
            <CardContent>
              <FormControlLabel
                control={
                  <Switch
                    checked={paramBool('regra_trava_ip')}
                    disabled={!podeEditar}
                    onChange={() => alternarParametro('regra_trava_ip', param('regra_trava_ip')?.valor)}
                  />
                }
                label={<Typography fontWeight="600">Trava por IP ativa</Typography>}
              />
              <Alert severity="warning" sx={{ mt: 2 }}>
                Só ligue depois de cadastrar os terminais abaixo. Com a trava ativa e a lista
                incompleta, qualquer máquina não cadastrada para de bipar.
              </Alert>
              <FormControlLabel
                sx={{ mt: 2 }}
                control={
                  <Switch
                    checked={paramBool('registrar_origem')}
                    disabled={!podeEditar}
                    onChange={() => alternarParametro('registrar_origem', param('registrar_origem')?.valor)}
                  />
                }
                label={
                  <Box>
                    <Typography fontWeight="600">Registrar IP de origem nas bipagens</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Fase de coleta — alimenta a lista de IPs descobertos.
                    </Typography>
                  </Box>
                }
              />
            </CardContent>
          </Card>

          {descobertos.length > 0 && (
            <Card elevation={0} sx={{ borderRadius: 2, mb: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  <ComputerIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                  IPs detectados e ainda não cadastrados
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f8fafc' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>IP</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Bipes</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Func.</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Setores vistos</TableCell>
                        {podeEditar && <TableCell align="right">Cadastrar</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {descobertos.map(d => (
                        <TableRow key={d.ip} hover>
                          <TableCell><code>{d.ip}</code></TableCell>
                          <TableCell align="right">{d.bipagens}</TableCell>
                          <TableCell align="right">{d.funcionarios}</TableCell>
                          <TableCell sx={{ maxWidth: 320 }}>
                            <Typography variant="caption">{d.setores}</Typography>
                          </TableCell>
                          {podeEditar && (
                            <TableCell align="right">
                              <Button size="small" startIcon={<AddIcon />}
                                onClick={() => setTerminalEdit({ ip: d.ip, nome_terminal: '', unidade: 'MATRIZ', ativo: true })}>
                                Cadastrar
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          <Card elevation={0} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight="bold">Terminais autorizados</Typography>
                {podeEditar && (
                  <Button variant="contained" startIcon={<AddIcon />}
                    onClick={() => setTerminalEdit({ ip: '', nome_terminal: '', unidade: 'MATRIZ', ativo: true })}>
                    Novo terminal
                  </Button>
                )}
              </Box>
              {terminais.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <ComputerIcon sx={{ fontSize: 60, color: '#e2e8f0' }} />
                  <Typography color="text.secondary">
                    Nenhum terminal cadastrado. Deixe a coleta rodar e cadastre a partir da lista acima.
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f8fafc' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>IP / faixa</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Terminal</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Unidade</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Ativo</TableCell>
                        {podeEditar && <TableCell align="right">Ações</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {terminais.map(t => (
                        <TableRow key={t.id} hover>
                          <TableCell><code>{t.ip}</code></TableCell>
                          <TableCell>{t.nome_terminal || '—'}</TableCell>
                          <TableCell>{t.unidade}</TableCell>
                          <TableCell>
                            <Chip label={t.ativo ? 'Sim' : 'Não'} size="small"
                                  color={t.ativo ? 'success' : 'default'} />
                          </TableCell>
                          {podeEditar && (
                            <TableCell align="right">
                              <IconButton size="small" onClick={() => setTerminalEdit({ ...t, ativo: t.ativo === 1 || t.ativo === true })}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" onClick={() => removerTerminal(t.id)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* ================= ABA 5 — LOGS ================= */}
      {aba === ABA.LOGS && (
        <>
          <Card elevation={0} sx={{ borderRadius: 2, mb: 3 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={2}>
                  <TextField fullWidth size="small" type="date" label="De" InputLabelProps={{ shrink: true }}
                    value={filtroLog.data_inicio}
                    onChange={(e) => setFiltroLog(p => ({ ...p, data_inicio: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField fullWidth size="small" type="date" label="Até" InputLabelProps={{ shrink: true }}
                    value={filtroLog.data_fim}
                    onChange={(e) => setFiltroLog(p => ({ ...p, data_fim: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tipo</InputLabel>
                    <Select label="Tipo" value={filtroLog.tipo}
                      onChange={(e) => setFiltroLog(p => ({ ...p, tipo: e.target.value }))}>
                      <MenuItem value="">Todos</MenuItem>
                      {TIPOS_LOG.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField fullWidth size="small" label="Código do funcionário"
                    value={filtroLog.codigo_funcionario}
                    onChange={(e) => setFiltroLog(p => ({ ...p, codigo_funcionario: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Filial</InputLabel>
                    <Select label="Filial" value={filtroLog.unidade}
                      onChange={(e) => setFiltroLog(p => ({ ...p, unidade: e.target.value }))}>
                      <MenuItem value="">Todas</MenuItem>
                      {UNIDADES.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Origem</InputLabel>
                    <Select label="Origem" value={filtroLog.origem}
                      onChange={(e) => setFiltroLog(p => ({ ...p, origem: e.target.value }))}>
                      {ORIGENS_LOG.map(o => (
                        <MenuItem key={o.valor || 'todas'} value={o.valor}>{o.rotulo}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={1}>
                  <Button fullWidth variant="contained" onClick={() => setFiltroAplicado(filtroLog)}
                    disabled={carregandoLogs}>Filtrar</Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {resumo && resumo.por_funcionario?.length > 0 && (
            <Card elevation={0} sx={{ borderRadius: 2, mb: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Recusas por funcionário (critério para virar BLOQUEIO)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Fora dos casos já investigados, ninguém deveria ter mais de ~5 recusas na semana.
                  Acima disso, corrija a classificação de etapa ou a lista de setores antes de bloquear.
                </Typography>
                <Grid container spacing={1}>
                  {resumo.por_funcionario.slice(0, 12).map(f => (
                    <Grid item key={f.codigo_funcionario}>
                      <Chip
                        label={`${f.nome_funcionario || f.codigo_funcionario}: ${f.total}`}
                        color={f.total > 5 ? 'error' : 'default'}
                        variant={f.total > 5 ? 'filled' : 'outlined'}
                      />
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          )}

          <Card elevation={0} sx={{ borderRadius: 2 }}>
            <CardContent>
              {carregandoLogs && (
                <Box display="flex" justifyContent="center" py={2}><CircularProgress size={28} /></Box>
              )}
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight="bold">
                  Eventos ({logs.length})
                </Typography>
                <Button size="small" onClick={exportarLogs} disabled={!logs.length}>
                  Exportar CSV
                </Button>
              </Box>
              {logs.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <SettingsIcon sx={{ fontSize: 60, color: '#e2e8f0' }} />
                  <Typography color="text.secondary">Nenhum evento no período.</Typography>
                </Box>
              ) : (
                <TableContainer sx={{ maxHeight: 600 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Data</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Tipo</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Funcionário</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Requisição</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Setor</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Motivo</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>IP</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {logs.map(l => (
                        <TableRow key={l.id} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {new Date(l.data).toLocaleString('pt-BR')}
                          </TableCell>
                          <TableCell>
                            <Chip label={l.tipo} size="small"
                                  color={String(l.tipo).startsWith('RECUSA') ? 'error' : 'default'}
                                  variant={String(l.tipo).startsWith('RECUSA') ? 'filled' : 'outlined'} />
                          </TableCell>
                          <TableCell>{l.nome_funcionario || l.codigo_funcionario}</TableCell>
                          <TableCell>
                            {l.codigo_requisicao}
                            {l.origem === 'ordem_producao' && (
                              <Chip size="small" label="OP" color="info" variant="outlined"
                                sx={{ ml: 0.5, height: 18, fontSize: 11 }} />
                            )}
                          </TableCell>
                          <TableCell>{l.setor}</TableCell>
                          <TableCell sx={{ maxWidth: 300 }}>
                            <Tooltip title={l.motivo || ''}>
                              <Typography variant="caption" noWrap display="block">{l.motivo}</Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell><code>{l.ip_origem || '—'}</code></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* ============ ABAS MIGRADAS: telas completas embutidas ============ */}
      {aba === ABA.FUNCIONARIOS && <Funcionarios />}
      {aba === ABA.SETORES && <Setores />}
      {aba === ABA.USUARIOS && <CadastroUsuario />}

      {/* ============ DIALOG: editar etapa ============ */}
      <Dialog open={!!etapaEdit} onClose={() => setEtapaEdit(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Classificar setor</DialogTitle>
        <DialogContent>
          {etapaEdit && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField fullWidth label="Setor" value={etapaEdit.setor} disabled />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Etapa canônica" value={etapaEdit.etapa}
                  helperText="Setores com a mesma etapa contam como a mesma leitura"
                  onChange={(e) => setEtapaEdit(p => ({ ...p, etapa: e.target.value.toUpperCase() }))} />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Tipo</InputLabel>
                  <Select label="Tipo" value={etapaEdit.tipo}
                    onChange={(e) => setEtapaEdit(p => ({ ...p, tipo: e.target.value }))}>
                    {TIPOS_ETAPA.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Unidade</InputLabel>
                  <Select label="Unidade" value={etapaEdit.unidade}
                    onChange={(e) => setEtapaEdit(p => ({ ...p, unidade: e.target.value }))}>
                    {UNIDADES.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth type="number" label="Ordem no fluxo" value={etapaEdit.ordem}
                  inputProps={{ min: 1, max: 99 }}
                  onChange={(e) => setEtapaEdit(p => ({ ...p, ordem: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={!!etapaEdit.ativo}
                    onChange={(e) => setEtapaEdit(p => ({ ...p, ativo: e.target.checked }))} />}
                  label="Setor ativo" />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setEtapaEdit(null)}>Cancelar</Button>
          <Button variant="contained" onClick={salvarEtapa} disabled={salvando}
            startIcon={salvando ? <CircularProgress size={20} /> : null}>
            {salvando ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============ DIALOG: atrelar setores ============ */}
      <Dialog open={!!vinculoEdit} onClose={() => setVinculoEdit(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Setores de {vinculoEdit?.nome}</DialogTitle>
        <DialogContent>
          {vinculoEdit && (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Setores liberados</InputLabel>
              <Select
                multiple
                value={vinculoEdit.setoresSelecionados}
                onChange={(e) => setVinculoEdit(p => ({ ...p, setoresSelecionados: e.target.value }))}
                input={<OutlinedInput label="Setores liberados" />}
                renderValue={(sel) => sel.join(', ')}
                MenuProps={{ PaperProps: { style: { maxHeight: 400 } } }}
              >
                {setoresDisponiveis.map(s => (
                  <MenuItem key={s} value={s}>
                    <Checkbox checked={vinculoEdit.setoresSelecionados.indexOf(s) > -1} />
                    <ListItemText primary={s} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setVinculoEdit(null)}>Cancelar</Button>
          <Button variant="contained" onClick={salvarVinculo} disabled={salvando}
            startIcon={salvando ? <CircularProgress size={20} /> : null}>
            {salvando ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============ DIALOG: forma farmacêutica × etapa ============ */}
      <Dialog open={!!formaEdit} onClose={() => setFormaEdit(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {formaEdit?.novo ? 'Nova regra de forma' : `Regra da forma ${formaEdit?.forma_nome || formaEdit?.tpformafarma}`}
        </DialogTitle>
        <DialogContent>
          {formaEdit && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                {formaEdit.novo ? (
                  <FormControl fullWidth>
                    <InputLabel>Forma farmacêutica</InputLabel>
                    <Select
                      value={formaEdit.tpformafarma}
                      label="Forma farmacêutica"
                      onChange={(e) => {
                        const f = formasDisponiveis.find(x => x.tpformafarma === e.target.value);
                        setFormaEdit(p => ({
                          ...p, tpformafarma: e.target.value, forma_nome: f?.forma_nome || ''
                        }));
                      }}
                    >
                      {formasLivres.map(f => (
                        <MenuItem key={f.tpformafarma} value={f.tpformafarma}>
                          {f.forma_nome || `Forma ${f.tpformafarma}`} ({f.bipes_12m} bipes/12m)
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  // A forma e a chave do recurso: nao pode mudar na edicao.
                  <TextField fullWidth disabled label="Forma farmacêutica"
                    value={`${formaEdit.forma_nome || ''} (#${formaEdit.tpformafarma})`} />
                )}
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Etapas que esta forma não pode bipar</InputLabel>
                  <Select
                    multiple
                    value={formaEdit.etapas}
                    onChange={(e) => setFormaEdit(p => ({ ...p, etapas: e.target.value }))}
                    input={<OutlinedInput label="Etapas que esta forma não pode bipar" />}
                    renderValue={(sel) => sel.join(', ')}
                    MenuProps={{ PaperProps: { style: { maxHeight: 400 } } }}
                  >
                    {etapasDisponiveis.map(e => (
                      <MenuItem key={e} value={e}>
                        <Checkbox checked={formaEdit.etapas.indexOf(e) > -1} />
                        <ListItemText primary={e} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary">
                  Qualquer bipe de uma fórmula desta forma nas etapas acima é recusado,
                  independente de ordem. {formaEdit.etapas.length} combinações serão gravadas,
                  todas com o mesmo estado do switch abaixo.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={2} label="Observação"
                  value={formaEdit.motivo}
                  onChange={(e) => setFormaEdit(p => ({ ...p, motivo: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={!!formaEdit.ativo}
                    onChange={(e) => setFormaEdit(p => ({ ...p, ativo: e.target.checked }))} />}
                  label="Regra ativa" />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setFormaEdit(null)}>Cancelar</Button>
          <Button variant="contained" onClick={salvarRegraForma}
            disabled={salvando || !formaEdit?.tpformafarma || !formaEdit?.etapas?.length}
            startIcon={salvando ? <CircularProgress size={20} /> : null}>
            {salvando ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============ DIALOG: ordem entre etapas ============ */}
      <Dialog open={!!regraEdit} onClose={() => setRegraEdit(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {regraEdit?.novo ? 'Nova regra de ordem' : `Regra do gatilho ${regraEdit?.etapa_gatilho}`}
        </DialogTitle>
        <DialogContent>
          {regraEdit && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                {regraEdit.novo ? (
                  <FormControl fullWidth>
                    <InputLabel>Etapa gatilho</InputLabel>
                    <Select
                      value={regraEdit.etapa_gatilho}
                      label="Etapa gatilho"
                      onChange={(e) => setRegraEdit(p => ({
                        ...p, etapa_gatilho: e.target.value,
                        bloqueadas: p.bloqueadas.filter(b => b !== e.target.value)
                      }))}
                    >
                      {gatilhosLivres.map(e => <MenuItem key={e} value={e}>{e}</MenuItem>)}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField fullWidth disabled label="Etapa gatilho" value={regraEdit.etapa_gatilho} />
                )}
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Etapas bloqueadas depois</InputLabel>
                  <Select
                    multiple
                    value={regraEdit.bloqueadas}
                    onChange={(e) => setRegraEdit(p => ({ ...p, bloqueadas: e.target.value }))}
                    input={<OutlinedInput label="Etapas bloqueadas depois" />}
                    renderValue={(sel) => sel.join(', ')}
                    MenuProps={{ PaperProps: { style: { maxHeight: 400 } } }}
                  >
                    {/* Auto-bloqueio e trabalho da regra de etapa duplicada:
                        some da lista em vez de so dar 400 no salvar. */}
                    {etapasDisponiveis.filter(e => e !== regraEdit.etapa_gatilho).map(e => (
                      <MenuItem key={e} value={e}>
                        <Checkbox checked={regraEdit.bloqueadas.indexOf(e) > -1} />
                        <ListItemText primary={e} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary">
                  Bipar {regraEdit.etapa_gatilho || 'o gatilho'} passa a impedir os bipes acima.
                  A ordem inversa continua liberada. {regraEdit.bloqueadas.length} combinações
                  serão gravadas, todas com o mesmo estado do switch abaixo.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={2} label="Observação"
                  value={regraEdit.motivo}
                  onChange={(e) => setRegraEdit(p => ({ ...p, motivo: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={!!regraEdit.ativo}
                    onChange={(e) => setRegraEdit(p => ({ ...p, ativo: e.target.checked }))} />}
                  label="Regra ativa" />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setRegraEdit(null)}>Cancelar</Button>
          <Button variant="contained" onClick={salvarRegraEtapa}
            disabled={salvando || !regraEdit?.etapa_gatilho || !regraEdit?.bloqueadas?.length}
            startIcon={salvando ? <CircularProgress size={20} /> : null}>
            {salvando ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============ DIALOG: terminal ============ */}
      <Dialog open={!!terminalEdit} onClose={() => setTerminalEdit(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{terminalEdit?.id ? 'Editar terminal' : 'Novo terminal'}</DialogTitle>
        <DialogContent>
          {terminalEdit && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="IP ou faixa" value={terminalEdit.ip}
                  helperText="Ex.: 192.168.0.50 ou 192.168.0.*"
                  onChange={(e) => setTerminalEdit(p => ({ ...p, ip: e.target.value }))} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Nome do terminal" value={terminalEdit.nome_terminal || ''}
                  onChange={(e) => setTerminalEdit(p => ({ ...p, nome_terminal: e.target.value }))} />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Unidade</InputLabel>
                  <Select label="Unidade" value={terminalEdit.unidade}
                    onChange={(e) => setTerminalEdit(p => ({ ...p, unidade: e.target.value }))}>
                    {UNIDADES.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={<Switch checked={!!terminalEdit.ativo}
                    onChange={(e) => setTerminalEdit(p => ({ ...p, ativo: e.target.checked }))} />}
                  label="Ativo" />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setTerminalEdit(null)}>Cancelar</Button>
          <Button variant="contained" onClick={salvarTerminal} disabled={salvando}
            startIcon={salvando ? <CircularProgress size={20} /> : null}>
            {salvando ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Configuracoes;
